import { useId, useState } from "react";
import { Plus, Trash2, Clock, Eye, Link2, Share2, CheckCircle2, ChevronDown } from "lucide-react";
import {
  useSharedLinks,
  useRevokeSharedLink,
  type SharedLinkSummary,
} from "../../lib/api/useSharedLinks";
import { useToast } from "../../lib/toast/ToastProvider";
import { ApiError } from "../../lib/api/client";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { ShareLinkDialog } from "../share/ShareLinkDialog";
import { SectionCard } from "../ui/SectionCard";
import { IconChip } from "../ui/IconChip";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { EmptyState } from "../ui/EmptyState";
import { SECTION_ACCENT } from "../../lib/accents";
import { effectiveVisibleSections } from "@shared/lib/visibleSections";
import { INLINE_STATUS, ROW, ROW_MUTED } from "../../lib/surfaces";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";
import { LinkAccessEventsPanel } from "./LinkAccessEventsPanel";

function formatRelative(iso: string | null): string {
  if (!iso) return "Never accessed";
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

function formatExpiration(iso: string | null): {
  label: string;
  expired: boolean;
} {
  if (!iso) return { label: "Never expires", expired: false };
  const date = new Date(iso);
  const expired = date.getTime() < Date.now();
  const label = date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return { label: expired ? `Expired ${label}` : `Expires ${label}`, expired };
}

export function SharingSection({ childId }: { childId: string }) {
  const { data: links, isLoading, error } = useSharedLinks(childId);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleting, setDeleting] = useState<SharedLinkSummary | undefined>();
  const revokeMutation = useRevokeSharedLink(childId);
  const { publish } = useToast();

  const handleRevoke = async () => {
    if (!deleting) return;
    try {
      await revokeMutation.mutateAsync(deleting.id);
      publish({
        variant: "success",
        title: "Shared link deleted successfully",
      });
      setDeleting(undefined);
    } catch (err) {
      publish({
        variant: "error",
        title: "Failed to delete shared link",
        description: err instanceof ApiError ? err.message : undefined,
      });
      setDeleting(undefined);
    }
  };

  const activeLinks = links?.filter((l) => l.isActive) ?? [];
  const inactiveLinks = links?.filter((l) => !l.isActive) ?? [];

  return (
    <section className="space-y-6">
      <SectionCard
        as="div"
        icon={Share2}
        title="Secure Sharing"
        accent={SECTION_ACCENT.sharing}
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            Create Share Link
          </Button>
        }
      >
        <p className="text-sm text-neutral-700">
          Create a shared link to give caregivers access to your child&apos;s
          information. Links automatically expire and can be revoked anytime.
        </p>
        <p className="mt-2 text-xs text-neutral-600">
          Changed your mind? Revoke access instantly from your dashboard. Links stop
          working immediately — no waiting period.
        </p>
      </SectionCard>

      {isLoading && (
        <div role="status" className={INLINE_STATUS}>
          <Spinner size="sm" />
          Loading shared links…
        </div>
      )}

      {error && (
        <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
          Failed to load shared links: {error.message}
        </p>
      )}

      {!isLoading && !error && (links?.length ?? 0) === 0 && (
        <EmptyState
          art="send"
          accent="violet"
          icon={Link2}
          title="No shared links yet"
          description="Create a secure link to share your child's care information with a caregiver."
        />
      )}

      {activeLinks.length > 0 && (
        <div>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">
            Access Monitoring · Shared care links ({activeLinks.length})
          </h3>
          <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:items-start">
            {activeLinks.map((link) => (
              <LinkCard
                key={link.id}
                link={link}
                onRevoke={() => setDeleting(link)}
              />
            ))}
          </ul>
        </div>
      )}

      {inactiveLinks.length > 0 && (
        <div>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">
            Revoked Links ({inactiveLinks.length})
          </h3>
          <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:items-start">
            {inactiveLinks.map((link) => (
              <LinkCard key={link.id} link={link} onRevoke={() => {}} />
            ))}
          </ul>
        </div>
      )}

      <ShareLinkDialog open={createOpen} onOpenChange={setCreateOpen} childId={childId} />

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(undefined)}
        title="Revoke share link"
        description={`Revoke access for ${deleting?.caregiverName ?? "this caregiver"}? The link will stop working immediately.`}
        confirmLabel="Revoke Access"
        destructive
        isPending={revokeMutation.isPending}
        onConfirm={() => {
          void handleRevoke();
        }}
      />
    </section>
  );
}

function LinkCard({
  link,
  onRevoke,
}: {
  link: SharedLinkSummary;
  onRevoke: () => void;
}) {
  const expiry = formatExpiration(link.expiresAt);
  // `legalName` is an opt-in on the link, not a section: count it apart, so the
  // parent can see at a glance which links reveal the legal name. A switched-off
  // section (`pickup`, while PICKUP_CONSENT_ENABLED is off) counts in neither number.
  const { legalName: legalNameShared, ...sectionFlags } = effectiveVisibleSections(
    link.visibleSections,
  );
  const visibleCount = Object.values(sectionFlags).filter(Boolean).length;
  const totalSections = Object.keys(sectionFlags).length;
  const [showLog, setShowLog] = useState(false);
  const logId = useId();
  return (
    <li className={link.isActive ? ROW : ROW_MUTED}>
        <div className="flex items-start gap-3">
          <IconChip
            icon={link.isActive ? Share2 : Link2}
            accent={SECTION_ACCENT.sharing}
            size="sm"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-neutral-900">
                {link.caregiverName}
              </h4>
              {!link.isActive && (
                <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-700">
                  Revoked
                </span>
              )}
            </div>
            {link.caregiverEmail && (
              <p className="mt-0.5 text-xs text-neutral-600">{link.caregiverEmail}</p>
            )}
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-neutral-600">
              <div className="flex items-center gap-1">
                <Eye className="h-3 w-3" aria-hidden />
                <span>
                  {link.accessCount} {link.accessCount === 1 ? "view" : "views"}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" aria-hidden />
                <span>Last accessed {formatRelative(link.lastAccessedAt)}</span>
              </div>
              <div className={expiry.expired ? "text-red-600" : ""}>{expiry.label}</div>
              {link.acknowledgedAt && (
                <div className="flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 className="h-3 w-3" aria-hidden />
                  <span>Acknowledged {formatRelative(link.acknowledgedAt)}</span>
                </div>
              )}
            </div>
            <p className="mt-2 text-[11px] text-neutral-600">
              {visibleCount} of {totalSections} sections shared
              {legalNameShared === true ? " · Legal name included" : ""} · Token:{" "}
              {link.tokenPrefix}…
            </p>
          </div>
          {link.isActive && (
            <button
              type="button"
              onClick={onRevoke}
              aria-label={`Revoke ${link.caregiverName}`}
              className={cn(
                "rounded-full p-1.5 text-neutral-600 transition hover:bg-red-50 hover:text-red-700",
                FOCUS_RING,
              )}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="mt-3 border-t border-sky-100 pt-3">
          <button
            type="button"
            onClick={() => setShowLog((prev) => !prev)}
            aria-expanded={showLog}
            aria-controls={logId}
            className={cn(
              "flex items-center gap-1 rounded text-xs font-medium text-neutral-600 transition hover:text-neutral-900",
              FOCUS_RING,
            )}
          >
            <ChevronDown
              className={cn("h-3.5 w-3.5 transition-transform", showLog && "rotate-180")}
              aria-hidden
            />
            {showLog ? "Hide access log" : "View access log"}
          </button>
          {showLog && (
            <div id={logId} className="mt-3">
              <LinkAccessEventsPanel linkId={link.id} />
            </div>
          )}
        </div>
    </li>
  );
}
